import type { AiChatMessage } from "@productlane/lib/src/ai"

export type ShadowState = "pending" | "declined" | "sent"

export const DESIGN_IDS = ["1", "2", "3", "4", "5"] as const
export type DesignId = (typeof DESIGN_IDS)[number]

export const SHADOW_STATES: readonly ShadowState[] = [
	"pending",
	"declined",
	"sent",
]

export const isDesignId = (value: string | null): value is DesignId =>
	!!value && (DESIGN_IDS as readonly string[]).includes(value)

export const isShadowState = (value: string | null): value is ShadowState =>
	!!value && (SHADOW_STATES as readonly string[]).includes(value)

const todayAt = (hours: number, minutes: number) => {
	const date = new Date()
	date.setHours(hours, minutes, 0, 0)
	return date
}

export const AGENT_NAME = "Productlane Agent"

export const CUSTOMER = {
	name: "Maya Chen",
	email: "maya@kestrelfreight.com",
	company: "Kestrel Freight",
}

export const TEAMMATE = {
	name: "Jonas Weber",
	email: "jonas@relaybase.dev",
}

export const THREAD = {
	number: 4821,
	title: "Webhook deliveries fail with 401 after secret rotation",
	createdAt: todayAt(10, 2),
}

export interface DummyMessage {
	id: string
	author: "customer" | "teammate"
	at: Date
	html: string
}

export const CONVERSATION: DummyMessage[] = [
	{
		id: "m1",
		author: "customer",
		at: todayAt(10, 2),
		html: "<p>Hi, our webhook deliveries started failing at 9:40 this morning. Every event on the dashboard shows a 401 from our endpoint.</p>",
	},
	{
		id: "m2",
		author: "customer",
		at: todayAt(10, 3),
		html: "<p>The only change on our side: we rotated the signing secret under Settings → Webhooks and deployed the new value at 9:55.</p>",
	},
	{
		id: "m3",
		author: "teammate",
		at: todayAt(10, 6),
		html: "<p>Thanks Maya, I'm on it. Is it only the production endpoint, the one ending in /hooks/relaybase?</p>",
	},
	{
		id: "m4",
		author: "customer",
		at: todayAt(10, 9),
		html: "<p>Yes, only production. Staging still works, but we didn't rotate that one.</p><p>Does the old secret keep working for a while, or did we break something?</p>",
	},
]

export const ASSIGNED_AT = todayAt(10, 5)

export const SHADOW_REPLY = {
	at: todayAt(10, 9),
	html: [
		"<p>Hi Maya, thanks for the details. When you rotate a signing secret, Relaybase signs every delivery with the new secret right away, so the old one stops working at once.</p>",
		"<p>The 401s mean your endpoint still verifies with the old value. Check that every instance of your service loaded the new <code>RELAYBASE_SIGNING_SECRET</code> after the 9:55 deploy. A cached config or a replica that didn't restart often keeps the old secret.</p>",
		"<p>Once verification passes, replay the failed events from Webhooks → Deliveries → Replay failed.</p>",
	].join(""),
}

export const SEEDED_QUESTION =
	"How do you suggest we improve the agent's response next time?"

export const ASSISTANT_THINKING_LABEL = "Reading the agent's guidance"

export const ASSISTANT_ANSWER_HTML = [
	"<p>The draft told Maya the old secret stops working at once. Relaybase signs with both secrets for 24 hours after a rotation. Her 401s come from code that checks only the first signature in the header.</p>",
	"<p>The agent guessed because nothing it reads covers the overlap. Two changes would have produced the right answer:</p>",
].join("")

export const INSTRUCTION_CHANGE = {
	existing: [
		"Answer in the customer's language.",
		"Link the matching docs article when one exists.",
	],
	added:
		"After a signing secret rotation, Relaybase signs every delivery with the old and the new secret for 24 hours. Tell customers to accept either v1 signature in the Relaybase-Signature header during that window.",
	reason: "From #4821. The draft said the old secret stops working at once.",
}

export const LINEAR_PROPOSAL = {
	identifier: "PLAT-1482",
	title: "Give the Productlane Agent read access to webhook delivery logs",
	team: "Platform",
	label: "Agent data source",
	thread: "#4821",
	why: "The agent can't see delivery attempts, so it guessed why a customer's deliveries returned 401.",
	steps: [
		"Create a read-only Postgres role with SELECT on webhook_deliveries.",
		"Connect it in Productlane under Settings → Integrations → PostgreSQL.",
		"Whitelist the query below. The agent passes the endpoint id it reads from the contact.",
	],
	sql: "SELECT status, response_code, signature_count, attempted_at\nFROM webhook_deliveries\nWHERE endpoint_id = $1\nORDER BY attempted_at DESC\nLIMIT 20",
}

export const DECLINE_REASONS = [
	"Wrong facts",
	"Missing data",
	"Tone",
	"Needs a human",
] as const

const assistantMessageBase = {
	actions: [],
	sources: [],
	createdAt: todayAt(10, 11),
} satisfies Partial<AiChatMessage>

export const seededQuestionMessage: AiChatMessage = {
	...assistantMessageBase,
	id: "shadow-seed",
	role: "user",
	content: SEEDED_QUESTION,
	processed: true,
}

export const assistantAnswerMessage = (processed: boolean): AiChatMessage => ({
	...assistantMessageBase,
	id: "shadow-answer",
	role: "assistant",
	content: processed ? ASSISTANT_ANSWER_HTML : "",
	processed,
	waitAction: processed ? undefined : ASSISTANT_THINKING_LABEL,
})
